import type { User } from '../types';

// Browser-only Login with Amazon (authorization code + PKCE): static hosting has no server to keep a client secret.
const LWA_CLIENT_ID = 'amzn1.application-oa2-client.e03b3dcae4a744929049694c2f7f420e';
// Must match an Allowed Return URL registered for the LWA security profile.
export const LWA_REDIRECT_URI = 'https://www.amazononlines.com/api/auth/amazon/callback';

const AUTHORIZE_URL = 'https://www.amazon.com/ap/oa';
const TOKEN_URL = 'https://api.amazon.com/auth/o2/token';
const TOKENINFO_URL = 'https://api.amazon.com/auth/o2/tokeninfo';
const PROFILE_URL = 'https://api.amazon.com/user/profile';
const STATE_COOKIE = 'lwa_state';
const VERIFIER_COOKIE = 'lwa_verifier';

// The apex and www hosts are different origins, so the cookies are shared across both.
const cookieDomain = (): string =>
  /(^|\.)amazononlines\.com$/i.test(window.location.hostname) ? '; domain=.amazononlines.com' : '';

const setCookie = (name: string, value: string, maxAge: number): void => {
  document.cookie = `${name}=${value}; max-age=${maxAge}; path=/; SameSite=Lax; Secure${cookieDomain()}`;
};

const readCookie = (name: string): string | null => {
  const match = document.cookie.split('; ').find((c) => c.startsWith(`${name}=`));
  return match ? match.slice(name.length + 1) : null;
};

const base64Url = (bytes: Uint8Array): string =>
  btoa(String.fromCharCode(...bytes)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');

const randomToken = (byteLength: number): string => base64Url(crypto.getRandomValues(new Uint8Array(byteLength)));

const sha256Challenge = async (verifier: string): Promise<string> =>
  base64Url(new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier))));

export const startAmazonLogin = async (): Promise<void> => {
  const state = randomToken(24);
  const verifier = randomToken(48);
  setCookie(STATE_COOKIE, state, 300);
  setCookie(VERIFIER_COOKIE, verifier, 300);

  const params = new URLSearchParams({
    client_id: LWA_CLIENT_ID,
    scope: 'profile',
    response_type: 'code',
    redirect_uri: LWA_REDIRECT_URI,
    state,
    code_challenge: await sha256Challenge(verifier),
    code_challenge_method: 'S256',
  });
  window.location.href = `${AUTHORIZE_URL}?${params.toString()}`;
};

export class AmazonLoginError extends Error {
  constructor(public readonly code: 'access_denied' | 'invalid_request' | 'state_mismatch' | 'token_failed' | 'profile_failed') {
    super(code);
    this.name = 'AmazonLoginError';
  }
}

interface LwaProfile {
  user_id: string;
  name?: string;
  email?: string;
}

export const completeAmazonLogin = async (): Promise<{ user: User; token: string }> => {
  const query = new URLSearchParams(window.location.search);
  const fragment = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  const read = (key: string): string | null => query.get(key) ?? fragment.get(key);

  const expectedState = readCookie(STATE_COOKIE);
  const verifier = readCookie(VERIFIER_COOKIE);
  setCookie(STATE_COOKIE, '', 0);
  setCookie(VERIFIER_COOKIE, '', 0);

  if (read('error') === 'access_denied') throw new AmazonLoginError('access_denied');

  const code = read('code');
  const state = read('state');
  if (!code || !state || !verifier) throw new AmazonLoginError('invalid_request');
  if (!expectedState || state !== expectedState) throw new AmazonLoginError('state_mismatch');

  const tokenRes = await fetch(TOKEN_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'authorization_code',
      code,
      client_id: LWA_CLIENT_ID,
      redirect_uri: LWA_REDIRECT_URI,
      code_verifier: verifier,
    }).toString(),
  });
  if (!tokenRes.ok) throw new AmazonLoginError('token_failed');
  const { access_token: token } = (await tokenRes.json()) as { access_token?: string };
  if (!token) throw new AmazonLoginError('token_failed');

  // Confirm the token was issued to this app, not another LWA client.
  const infoRes = await fetch(`${TOKENINFO_URL}?access_token=${encodeURIComponent(token)}`);
  const info = infoRes.ok ? ((await infoRes.json()) as { aud?: string }) : {};
  if (info.aud !== LWA_CLIENT_ID) throw new AmazonLoginError('token_failed');

  const profileRes = await fetch(PROFILE_URL, { headers: { Authorization: `bearer ${token}` } });
  if (!profileRes.ok) throw new AmazonLoginError('profile_failed');
  const profile = (await profileRes.json()) as LwaProfile;

  const [firstName, ...rest] = (profile.name ?? '').split(' ');
  const user: User = {
    id: `amazon_${profile.user_id}`,
    email: profile.email ?? '',
    firstName: firstName || undefined,
    lastName: rest.join(' ') || undefined,
    createdAt: new Date().toISOString(),
  };
  return { user, token };
};
